/**
 * Structured data the public pages print as inline JSON-LD: every page's
 * Organization and WebSite, the FAQPage of /data and /pricing, a raise
 * page's WebPage (an FAQPage too where it prints questions) about its
 * issuer, or a profile's or compare page's about its platforms, the
 * SoftwareApplication of /pricing, the CollectionPage (an ItemList of the
 * raises shown) of /raises and its hubs, the CollectionPage (an ItemList of
 * the platforms, as Organizations) of /platforms, the Dataset of the statistics
 * page, and the BreadcrumbList of the raise pages, /raises, the hubs, the
 * profiles, the compare pages and the statistics page. Pure, and
 * loaded by the browser too: /pricing's script redraws its
 * SoftwareApplication in the visitor's currency.
 */

import type {
  BreadcrumbList,
  CollectionPage,
  Dataset,
  FAQPage,
  FAQPageLeaf,
  Graph,
  ListItem,
  MergeLeafTypes,
  Offer,
  Organization,
  Question,
  SoftwareApplication,
  WebPageLeaf,
  WebSite,
  WithContext,
} from 'schema-dts';
import { BRAND_NAME } from '../../shared/brand';

/**
 * Inline JSON for a <script> element: `<` escaped, so no string in it can
 * close the element early.
 */
export function jsonForScript(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

/** One question and its answer, as the page prints them. */
export interface QuestionAnswer {
  question: string;
  answer: string;
}

/** A moment as schema.org's DateTime: ISO 8601 in UTC, to the second. */
export function isoDateTime(at: Date): string {
  return at.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** The questions as schema.org's Questions, each in the words the page prints. */
function questionsOf(items: QuestionAnswer[]): Question[] {
  return items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  }));
}

/**
 * An FAQPage of the questions a page shows, each in the words it prints,
 * as Google asks: the markup's text is the visible text.
 */
export function faqPageJsonLd(items: QuestionAnswer[]): string {
  const page: WithContext<FAQPage> = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questionsOf(items),
  };
  return jsonForScript(page);
}

/**
 * A company as a page names it: the issuer a raise page is about, a
 * platform /platforms lists.
 */
export interface PageCompany {
  /** As the page prints it: a raise page's h1, a directory row's name. */
  name: string;
  /** ISO 3166-1 alpha-2, "GB", where the page names the country; else null. */
  countryCode: string | null;
  /** Our copy of its logo, absolute, where the page draws it; else null. */
  logo: string | null;
}

/**
 * The company as an Organization: its name, the country it is based in and
 * our copy of its logo, each only where known. No `url`: no raise row
 * stores the issuer's own website, and a platform's is linked only for a
 * reader signed in.
 */
export function companyOrganization(company: PageCompany): Organization {
  return {
    '@type': 'Organization',
    name: company.name,
    ...(company.countryCode
      ? {
          address: {
            '@type': 'PostalAddress',
            addressCountry: company.countryCode,
          },
        }
      : {}),
    ...(company.logo ? { logo: company.logo } : {}),
  };
}

/** A WebPage that prints questions: an FAQPage as well, in one node. */
type WebFaqPage = MergeLeafTypes<[WebPageLeaf, FAQPageLeaf]>;

/**
 * A page about one company, a raise page, or two side by side, a compare
 * page: its title, address, meta description and when what it says last
 * changed, part of the site's WebSite (by the @id siteJsonLd gives it), about
 * the issuer or the platforms. Where the
 * page prints questions (`faq`, null where it prints none) the same node
 * is an FAQPage too, its questions in the words the page prints, so there
 * is one page node either way and never an FAQPage without the questions
 * on screen. `origin` is the public site's, no trailing slash.
 */
export function webPageJsonLd(page: {
  name: string;
  url: string;
  description: string;
  /** Null where nothing dates it: the node then says nothing. */
  dateModified: Date | null;
  origin: string;
  about: PageCompany | PageCompany[];
  faq: QuestionAnswer[] | null;
}): string {
  const common = {
    name: page.name,
    url: page.url,
    description: page.description,
    ...(page.dateModified
      ? { dateModified: isoDateTime(page.dateModified) }
      : {}),
    isPartOf: { '@id': websiteId(page.origin) },
    about: Array.isArray(page.about)
      ? page.about.map(companyOrganization)
      : companyOrganization(page.about),
  };
  const node: WithContext<WebPageLeaf> | WithContext<WebFaqPage> = page.faq
    ? {
        '@context': 'https://schema.org',
        '@type': ['WebPage', 'FAQPage'],
        ...common,
        mainEntity: questionsOf(page.faq),
      }
    : { '@context': 'https://schema.org', '@type': 'WebPage', ...common };
  return jsonForScript(node);
}

/** One price a page lists, as its Offer states it. */
export interface ListedPrice {
  /** "Alerts, billed monthly"; a plan's name where it costs nothing. */
  name: string;
  /** In minor units (cents, pence), as the catalogue holds it. */
  amount: number;
  /** ISO 4217, in either case: "usd". */
  currency: string;
  /** How often it is charged; null for a plan that costs nothing. */
  every: 'month' | 'year' | null;
  /** Whether the amount holds the tax; null where there is none. */
  taxIncluded: boolean | null;
}

/** An amount in minor units as schema.org's price: 1500 is 15, 825 is 8.25. */
export function schemaPrice(minor: number): number {
  return minor / 100;
}

const BILLING_DURATION = { month: 'P1M', year: 'P1Y' } as const;

/**
 * The product as one SoftwareApplication, its offers the prices a page
 * lists, in the order it lists them. A price charged every month or year
 * says so in a UnitPriceSpecification, with whether the amount holds the
 * tax; a free plan is the bare Offer.
 *
 * Never an aggregateRating or a review. Google's software app page asks for
 * one, but its Rich Results Test calls aggregateRating optional and passes
 * the item without it (31 Oct 2026). The site has no ratings or reviews,
 * and markup with them would be inventing them.
 */
export function softwareApplicationJsonLd(prices: ListedPrice[]): string {
  const offers: Offer[] = prices.map((p) => {
    const price = schemaPrice(p.amount);
    const priceCurrency = p.currency.toUpperCase();
    return {
      '@type': 'Offer',
      name: p.name,
      price,
      priceCurrency,
      ...(p.every === null
        ? {}
        : {
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price,
              priceCurrency,
              billingDuration: BILLING_DURATION[p.every],
              ...(p.taxIncluded === null
                ? {}
                : { valueAddedTaxIncluded: p.taxIncluded }),
            },
          }),
    };
  });
  const app: WithContext<SoftwareApplication> = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: BRAND_NAME,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    offers,
  };
  return jsonForScript(app);
}

/** One step of a breadcrumb trail, as the page prints it. */
export interface Crumb {
  name: string;
  /** From the site's root: "/raises". Null for the page itself, which is not a link. */
  href: string | null;
}

/**
 * A BreadcrumbList of the trail a page shows, step for step: the same names,
 * in the same order, each linked step's address made absolute on `origin`
 * ("https://raisesonar.com", no trailing slash). The page itself, the last
 * step, is no link on the page, so it has no `item` here either, which
 * Google reads as the page's own address.
 */
export function breadcrumbListJsonLd(trail: Crumb[], origin: string): string {
  const steps: ListItem[] = trail.map((crumb, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: crumb.name,
    ...(crumb.href === null ? {} : { item: `${origin}${crumb.href}` }),
  }));
  const list: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: steps,
  };
  return jsonForScript(list);
}

/** One entry of a list page, as the page links it. */
export interface ListedPage {
  name: string;
  /** From the site's root: "/raises/loopwell". */
  href: string;
}

/** How many of a list page's entries its ItemList names: the first, in the page's order. */
export const LIST_ITEMS_LIMIT = 50;

/**
 * A page that lists other pages (/raises, a hub), with an ItemList of the
 * first LIST_ITEMS_LIMIT it shows, in the order it shows them, each address
 * absolute on `origin`. `numberOfItems` is every entry the page shows, as
 * schema.org has it for a list described in part. `url` is the page's
 * canonical address.
 */
export function collectionPageJsonLd(page: {
  name: string;
  description: string;
  url: string;
  items: ListedPage[];
  origin: string;
}): string {
  const entries: ListItem[] = page.items
    .slice(0, LIST_ITEMS_LIMIT)
    .map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: `${page.origin}${item.href}`,
    }));
  const collection: WithContext<CollectionPage> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: page.name,
    description: page.description,
    url: page.url,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: page.items.length,
      itemListElement: entries,
    },
  };
  return jsonForScript(collection);
}

/**
 * Everything a list page says about itself, in the order head.hbs prints
 * it: the CollectionPage of the entries it shows (`items`, in its order, the
 * first LIST_ITEMS_LIMIT in its ItemList), then the BreadcrumbList of the
 * trail it draws. Every page that lists raises makes its JSON-LD here
 * (RaisesPageService.view, which /raises and every hub render through), so
 * a list page added there has it too. `url` is the page's canonical;
 * `origin` the public site's, no trailing slash.
 */
export function listPageJsonLd(page: {
  name: string;
  description: string;
  url: string;
  items: ListedPage[];
  trail: Crumb[];
  origin: string;
}): string[] {
  return [
    collectionPageJsonLd(page),
    breadcrumbListJsonLd(page.trail, page.origin),
  ];
}

/**
 * A page that lists companies, each linking a page of ours (/platforms, whose
 * names link their profiles): a CollectionPage whose ItemList names every one
 * it shows, in the order it shows them, each entry the address it links and an
 * Organization as companyOrganization writes it, which has no url of its own
 * (that would be the company's site). `url` is the page's canonical.
 */
export function companyListJsonLd(page: {
  name: string;
  description: string;
  url: string;
  companies: (PageCompany & { href: string })[];
  origin: string;
}): string {
  const entries: ListItem[] = page.companies.map(({ href, ...company }, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `${page.origin}${href}`,
    item: companyOrganization(company),
  }));
  const collection: WithContext<CollectionPage> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: page.name,
    description: page.description,
    url: page.url,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: page.companies.length,
      itemListElement: entries,
    },
  };
  return jsonForScript(collection);
}

/** Creative Commons Attribution 4.0, as a Dataset's `license` names it. */
export const CC_BY_4 = 'https://creativecommons.org/licenses/by/4.0/';

/**
 * A page of figures anyone may reuse, as Google's Dataset search reads one: what it
 * is, the days it covers, who made it (the site's Organization, by the @id
 * siteJsonLd gives it), its licence, and where to download it. `description` has to
 * run 50 to 5,000 characters. `origin` is the public site's, no trailing slash.
 */
export function datasetJsonLd(page: {
  name: string;
  description: string;
  url: string;
  /** ISO 8601 interval: "2025-11-01/2026-10-31". */
  temporalCoverage: string;
  dateModified: Date;
  variableMeasured: string[];
  keywords: string[];
  download: { url: string; encodingFormat: string; name: string };
  origin: string;
}): string {
  const home = `${page.origin}/`;
  const dataset: WithContext<Dataset> = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: page.name,
    description: page.description,
    url: page.url,
    temporalCoverage: page.temporalCoverage,
    dateModified: isoDateTime(page.dateModified),
    creator: {
      '@type': 'Organization',
      '@id': `${home}#organization`,
      name: BRAND_NAME,
      url: home,
    },
    license: CC_BY_4,
    isAccessibleForFree: true,
    keywords: page.keywords,
    variableMeasured: page.variableMeasured,
    distribution: [
      {
        '@type': 'DataDownload',
        name: page.download.name,
        contentUrl: page.download.url,
        encodingFormat: page.download.encodingFormat,
      },
    ],
    isPartOf: { '@id': websiteId(page.origin) },
  };
  return jsonForScript(dataset);
}

/**
 * The logo, from the site's root: public/icons/logo-512.png, the app icon at
 * 512 px (`npm run favicon`), served under /static.
 */
export const LOGO_PATH = '/static/icons/logo-512.png';

/** The site's WebSite node, by its @id: a page's `isPartOf` names it. */
export function websiteId(origin: string): string {
  return `${origin}/#website`;
}

/**
 * What every page says about the site and who runs it, one @graph for
 * head.hbs (SiteJsonLdLocals puts it on every page): the Organization, with
 * its logo, the canonical description and its profiles elsewhere (`sameAs`,
 * left out while there are none), and the WebSite it publishes, which is
 * where Google reads the name it shows beside a result. Each is named by an
 * @id on the home page's address. No SearchAction: the public site has no
 * search address to give it. `origin` is the public site's, no trailing
 * slash: "https://raisesonar.com".
 */
export function siteJsonLd(site: {
  origin: string;
  description: string;
  sameAs: readonly string[];
}): string {
  const home = `${site.origin}/`;
  const organizationId = `${home}#organization`;
  const organization: Organization = {
    '@type': 'Organization',
    '@id': organizationId,
    name: BRAND_NAME,
    url: home,
    logo: `${site.origin}${LOGO_PATH}`,
    description: site.description,
    ...(site.sameAs.length > 0 ? { sameAs: [...site.sameAs] } : {}),
  };
  const website: WebSite = {
    '@type': 'WebSite',
    '@id': websiteId(site.origin),
    name: BRAND_NAME,
    url: home,
    publisher: { '@id': organizationId },
  };
  const graph: Graph = {
    '@context': 'https://schema.org',
    '@graph': [organization, website],
  };
  return jsonForScript(graph);
}
